/* Screen 1 - Policy Details */

import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import { toast } from "sonner";
import redflag from "../../../../assets/images/redflag.svg";
import Input from "../../../../components/common/Input/Input";
import SelectInput from "../../../../components/common/selectInput/selectInput";
import TitleDescription from "../../../../components/newMotorPolicy/titleDescription/titleDescription";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import {
  fetchOccupationsList,
  selectOccupations,
  selectOccupationsLoader,
} from "../../../../redux/features/constants/occupationsListSlice";
import { policyPremium } from "../../../../redux/features/motor/renewalPremiumSlice";
import { getClientSource } from "../../../../utils/getClientSource";
import RenewalLayout from "../../components/RenewalLayout";

const PolicyDetailsPage = ({
  policyDetailsInfo,
  onContinue,
  onBack,
  formData,
  setFormData,
  premiumData,
  setPremiumData,
  premiumError,
  setPremiumError,
}) => {
  const translate = useLanguageTranslate();
  const dispatch = useDispatch();

  // Get occupations from Redux
  const occupationsData = useSelector(selectOccupations);
  const occupationsLoading = useSelector(selectOccupationsLoader);

  // Local state for validation error and premium API
  const [policyLimitError, setPolicyLimitError] = useState("");
  const [premiumLoading, setPremiumLoading] = useState(false);
  const [isDebouncing, setIsDebouncing] = useState(false);

  // Fetch occupations list if not already loaded
  useEffect(() => {
    if (!occupationsData || !Array.isArray(occupationsData) || occupationsData.length === 0) {
      dispatch(fetchOccupationsList());
    }
  }, [dispatch, occupationsData]);

  // Pre-fill policy limit from policyView on mount
  useEffect(() => {
    if (policyDetailsInfo?.SumInsured && !formData.policyLimit) {
      const sumInsured = policyDetailsInfo.SumInsured;
      // Format with commas
      const formattedValue = Number(sumInsured).toLocaleString("en-US");
      setFormData((prev) => ({
        ...prev,
        policyLimit: formattedValue,
        originalPolicyLimit: sumInsured, // Store original value for comparison
      }));
    }
  }, [policyDetailsInfo, formData.policyLimit, setFormData]);

  // Pre-fill occupation from policyView on mount (API now returns the occupation code)
  useEffect(() => {
    if (policyDetailsInfo?.Occupation && !formData.occupation) {
      setFormData((prev) => ({
        ...prev,
        occupation: policyDetailsInfo.Occupation.toString(),
      }));
    }
  }, [policyDetailsInfo, formData.occupation, setFormData]);

  // Transform occupations data for SelectInput
  const occupationOptions = Array.isArray(occupationsData)
    ? occupationsData.map((occupation) => ({
        id: occupation.Code,
        value: occupation.Code.toString(),
        label: translate(occupation.Name, occupation.NameAr),
      }))
    : [];

  // Fetch premium for selected policy details (Real-time API call with validation)
  const fetchPremiumForPolicy = async () => {
    setPremiumLoading(true);
    setPremiumError(null);

    try {
      const payload = {
        PolicyNumber: policyDetailsInfo?.PolicyNumber,
        Channel: getClientSource(),
        DefaultCovers: true,
      };

      // Get numeric policy limit
      const cleanPolicyLimit = formData.policyLimit?.replace(/[^\d]/g, "") || "";
      const numericLimit = Number(cleanPolicyLimit);

      // Add NewSumInsured if different from original
      if (numericLimit !== Number(formData.originalPolicyLimit)) {
        payload.NewSumInsured = numericLimit.toString();
      }

      // Add Occupation if provided
      if (formData.occupation) {
        payload.Occupation = formData.occupation;
      }

      console.log("✅ Calling premium API:", payload);

      // Call premium API via Redux
      const premiumAction = await dispatch(policyPremium(payload));
      const premiumResponse = unwrapResult(premiumAction);

      setPremiumData(premiumResponse);
      console.log("✅ Premium calculated:", premiumResponse);

    } catch (error) {
      console.error("❌ Premium API error:", error);
      setPremiumError(error.message);
      toast.error(translate(
        "Failed to calculate premium. Please try again.",
        "فشل في حساب القسط. يرجى المحاولة مرة أخرى."
      ));
    } finally {
      setPremiumLoading(false);
      setIsDebouncing(false); // Re-enable continue button after API completes
      console.log("✅ Debouncing completed - continue button enabled");
    }
  };

  // Debounced premium API call when policy limit or occupation changes
  useEffect(() => {
    // Only fetch if we have a valid policy limit
    const cleanPolicyLimit = formData.policyLimit?.replace(/[^\d]/g, "") || "";
    const numericLimit = Number(cleanPolicyLimit);

    if (!cleanPolicyLimit || numericLimit === 0) {
      setIsDebouncing(false);
      return; // Don't fetch if no policy limit
    }

    // Validate min/max value before debouncing
    if (numericLimit < 1000 || numericLimit > 25000) {
      setIsDebouncing(false);
      return; // Don't fetch if validation fails
    }

    // Start debouncing - disable continue button
    setIsDebouncing(true);
    console.log("⏳ Debouncing started - continue button disabled");

    // Debounce the API call - wait 3 seconds after last change
    const debounceTimer = setTimeout(() => {
      console.log("⏰ Debounce completed, fetching premium...");
      fetchPremiumForPolicy();
    }, 3000); // 3 second debounce

    // Cleanup function to cancel the timeout if values change again
    return () => {
      console.log("🔄 Policy details changed, resetting debounce timer");
      clearTimeout(debounceTimer);
    };
  }, [formData.policyLimit, formData.occupation]);

  // Handle policy limit change with formatting
  const handlePolicyLimitChange = (e) => {
    const inputValue = e.target.value;
    // Remove any non-digit characters (including spaces and commas)
    const cleanValue = inputValue.replace(/[^\d]/g, "");

    if (!cleanValue) {
      setFormData((prev) => ({ ...prev, policyLimit: "" }));
      setPolicyLimitError("");
      return;
    }

    const numericValue = Number(cleanValue);

    // Validate min/max value
    if (numericValue < 1000) {
      setPolicyLimitError(
        translate(
          "Policy limit should be BHD 1,000 or higher.",
          "يجب ألا يقل مبلغ التأمين عن 1,000 د.ب."
        )
      );
    } else if (numericValue > 25000) {
      setPolicyLimitError(
        translate(
          "Policy limit cannot exceed BHD 25,000.",
          "مبلغ التأمين لا يمكن أن يتجاوز 25,000 دينار بحريني."
        )
      );
    } else {
      setPolicyLimitError("");
    }

    // Format with commas
    const formattedValue = numericValue.toLocaleString("en-US");
    setFormData((prev) => ({ ...prev, policyLimit: formattedValue }));
  };

  // Handle occupation change
  const handleOccupationChange = (e) => {
    setFormData((prev) => ({ ...prev, occupation: e.target.value }));
  };

  // Handle continue - validate and proceed
  const handleContinue = () => {
    // Validate policy limit
    const cleanPolicyLimit = formData.policyLimit?.replace(/[^\d]/g, "") || "";
    const numericLimit = Number(cleanPolicyLimit);

    if (!cleanPolicyLimit || numericLimit === 0) {
      setPolicyLimitError(
        translate("Policy limit is required", "مبلغ التأمين مطلوب")
      );
      return;
    }

    if (numericLimit < 1000) {
      setPolicyLimitError(
        translate(
          "Policy limit should be BHD 1,000 or higher.",
          "يجب ألا يقل مبلغ التأمين عن 1,000 د.ب."
        )
      );
      return;
    }

    if (numericLimit > 25000) {
      setPolicyLimitError(
        translate(
          "Policy limit cannot exceed BHD 25,000.",
          "مبلغ التأمين لا يمكن أن يتجاوز 25,000 دينار بحريني."
        )
      );
      return;
    }

    onContinue();
  };

  // Use pricing from premium API if available, fallback to policyDetailsInfo
  const basePremium = premiumData?.Premium || policyDetailsInfo?.Premium || 0;
  const VAT = premiumData?.Tax || policyDetailsInfo?.Tax || 0;
  const totalPrice = premiumData?.TotalPremium || policyDetailsInfo?.TotalPremium || (basePremium + VAT);

  return (
    <div className="mainCarDetailsModal pt-[1%] pb-[5%] max767:w-full max767:overflow-y-scroll max767:pb-[30%] max767:h-screen ipad:pt-[10%] ipad:h-[93vh] ipad:overflow-y-scroll">
      <RenewalLayout
        policyType="personalAccident"
        show_close_btn={false}
        is_hide_back_btn={false}
        backClickHandler={onBack}
        is_bottom_sticky_layout={true}
        show_summary={premiumData && !premiumError}
        btn_label={translate("Continue", "التالي")}
        btn_onClick={handleContinue}
        btn_disabled={!!policyLimitError || premiumLoading || isDebouncing}
        tempPrice={premiumData && !premiumError ? totalPrice : undefined}
      >
        <div className="flex flex-col items-center mx-auto max-w-lg max767:px-4 pt-[0%]">
          <div className="flex justify-center items-center w-full">
            <div className="p-0 w-full">
              {/* Title and Description */}
              <div className="max767:mt-2">
                <TitleDescription
                  title={translate("Policy Details", "تفاصيل السياسة")}
                  description={translate(
                    "Choose whether to keep or update your policy details.",
                    "اختر ما إذا كنت تريد الاحتفاظ بتفاصيل سياستك أو تحديثها."
                  )}
                />
              </div>

              {/* Form Fields */}
              <div className="mt-8 space-y-6">
                {/* Policy Limit Input */}
                <div>
                  <label className="block mb-2 text-[#48535B] text-base not-italic font-normal leading-[26px] tracking-[-0.16px]">
                    {translate("Policy Limit", "سقف السياسة")}{" "}
                    <span className="text-[#84919A] text-sm">
                      ({translate("max BHD 25,000", "الحد الأقصى 25,000 د.ب")})
                    </span>
                  </label>
                  <Input
                    className="px-4 py-3"
                    endAdornment={
                      <div className="flex items-center">
                        <img src={redflag} alt="flag" className="w-5 h-5" />
                        <p className="ml-2 text-black text-lg not-italic font-normal leading-[26px] tracking-[-0.18px] rtl:mr-2 rtl:ml-0">
                          {translate("BHD", "د.ب")}
                        </p>
                      </div>
                    }
                    type="text"
                    name="policyLimit"
                    value={formData.policyLimit || ""}
                    handleChange={handlePolicyLimitChange}
                    error={policyLimitError}
                    placeHolder={translate("Enter policy limit", "أدخل مبلغ التأمين")}
                  />
                </div>

                {/* Occupation Dropdown */}
                <div>
                  <SelectInput
                    label={translate("Occupation", "المهنة")}
                    name="occupation"
                    formData={formData}
                    handleChange={handleOccupationChange}
                    options_data={occupationOptions}
                    default_placeholder={translate("Select Occupation", "اختر المهنة")}
                    disabled={occupationsLoading}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </RenewalLayout>
    </div>
  );
};

export default PolicyDetailsPage;
